import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Shared page header — the app-wide baseline: a bold `text-2xl` title, an optional graphite
 * action tile (inverted in dark) with its glyph in the action foreground, an optional subtitle,
 * and a top-aligned actions slot. The title block is `PageTitle`, which the Social Trends page
 * also uses inside its own two-row header.
 */
export function PageHeader({
  title,
  description,
  icon,
  actions,
}: {
  title: string;
  description?: ReactNode;
  /** Lucide icon rendered in the action tile to the left of the title. */
  icon?: LucideIcon;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <PageTitle title={title} description={description} icon={icon} />
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** The icon tile, title and tagline: the part every page header shares, Social Trends included. */
export function PageTitle({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-action">
          <Icon className="h-5 w-5 text-action-fg" aria-hidden="true" />
        </div>
      )}
      <div>
        <h1 className="text-2xl font-bold text-ink">{title}</h1>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      </div>
    </div>
  );
}
